import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  getDocs,
  serverTimestamp,
  onSnapshot,
  orderBy,
  limit,
  startAfter,
  and,
  Timestamp,
  type DocumentSnapshot,
  deleteField,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { startOfDay, endOfDay, addDays, subDays, format, getDay } from 'date-fns';

// TrackingInfo est défini dans les types de commande

import { getOrganizationId } from '@/shared/lib/brandUtils';
import { resolveShippingMethodConfigId } from '@/shared/utils/shippingMethodConfigId';
import { db } from '@/shared/config/firebase';
import { OrgaConfigurationRepository } from './orga-configuration-repository';
import {
  OrderData,
  PickupPoint,
  Product,
  FirebaseOrder,
  FirebaseOrderSchema,
  ProductSchema,
  CreditTransaction
} from '@/models';
import type { OrderMergeConfig } from '@/models/orgaConfiguration';
import { CustomerService } from './customer-service';

interface UpdateDeliveryInfoParams {
  orderId: string;
  deliveryMethod: string | null;
  shippingCost?: number;
  address?: string;
  fullAddress?: string;
  houseNumber?: string;
  addressLine2?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  pickupPoint?: PickupPoint;
  weight?: number;
  methodName?: string;
  methodDescription?: string;
  provider?: string; // API utilisée pour gérer les méthodes de livraison (sendcloud)
  methodId?: number; // ID générique de la méthode de livraison (provider-agnostic)
  estimatedHours?: number;
  minWeight?: number;
  maxWeight?: number;
  servicePointInput?: string;
  isHomeDelivery?: boolean;
  // Informations complètes de la méthode de livraison
  methodPrice?: number;
  methodCurrency?: string;
  methodStatus?: string;
}

/**
 * Pure Firebase repository for order operations
 * All data is validated using Zod schemas before returning
 */
export class OrderRepository {
  private static getCollectionName(): string {
    return 'orders'; // Maintenant toujours 'orders'
  }

  private static getOrganizationId(): string {
    return getOrganizationId();
  }

  /** Retourne true si le jour d (0=Dim, 6=Sam) appartient à la plage [start, end] (cyclique). */
  private static isDayInRange(day: number, start: number, end: number): boolean {
    if (start <= end) return day >= start && day <= end;
    return day >= start || day <= end; // Plage cyclique (ex: Vendredi-Dimanche = 5-0)
  }

  /**
   * Calcule la période de recherche pour les commandes fusionnables.
   * @returns { start, end, isInOpenDays, activeRange }
   * - start/end: bornes de la période (début 00:00, fin 23:59:59)
   * - isInOpenDays: true si aujourd'hui est dans une plage de jours ouverts
   * - activeRange: la plage spécifique qui est active (pour stocker dans le snapshot)
   * 
   * Logique:
   * - Si dans une plage de jours ouverts → période = toute la plage
   * - Sinon → période = aujourd'hui uniquement
   */
  private static getMergeDateRange(mergeConfig: OrderMergeConfig | undefined, refDate: Date = new Date()): { start: Date; end: Date; isInOpenDays: boolean; activeRange: { start: number; end: number } | null } {
    const todayDayOfWeek = getDay(refDate); // 0=Dim, 1=Lun, ..., 6=Sam
    const todayStart = startOfDay(refDate);
    const todayEnd = endOfDay(refDate);

    // Cherche si aujourd'hui est dans une plage de jours ouverts
    const ranges = mergeConfig?.mergeOpenDaysRanges ?? [];
    const matchingRange = ranges.find((r) => this.isDayInRange(todayDayOfWeek, r.start, r.end));

    if (matchingRange) {
      // Calcule le nombre de jours en arrière/avant pour couvrir la plage
      const daysBack = (todayDayOfWeek - matchingRange.start + 7) % 7;
      const daysForward = (matchingRange.end - todayDayOfWeek + 7) % 7;
      const periodStart = startOfDay(subDays(refDate, daysBack));
      const periodEnd = endOfDay(addDays(refDate, daysForward));
      console.log(`🗓️ Merge period (jours ouverts): ${format(periodStart, 'yyyy-MM-dd')} -> ${format(periodEnd, 'yyyy-MM-dd')}`);
      return { start: periodStart, end: periodEnd, isInOpenDays: true, activeRange: { start: matchingRange.start, end: matchingRange.end } };
    }

    // Legacy: mergeOpenDaysStart/End (rétrocompatibilité)
    const { mergeOpenDaysStart: legacyStart, mergeOpenDaysEnd: legacyEnd } = mergeConfig ?? {};
    if (legacyStart != null && legacyEnd != null && this.isDayInRange(todayDayOfWeek, legacyStart, legacyEnd)) {
      const daysBack = (todayDayOfWeek - legacyStart + 7) % 7;
      const daysForward = (legacyEnd - todayDayOfWeek + 7) % 7;
      const periodStart = startOfDay(subDays(refDate, daysBack));
      const periodEnd = endOfDay(addDays(refDate, daysForward));
      console.log(`🗓️ Merge period (legacy): ${format(periodStart, 'yyyy-MM-dd')} -> ${format(periodEnd, 'yyyy-MM-dd')}`);
      return { start: periodStart, end: periodEnd, isInOpenDays: true, activeRange: { start: legacyStart, end: legacyEnd } };
    }

    // Pas dans une plage de jours ouverts → aujourd'hui uniquement
    return { start: todayStart, end: todayEnd, isInOpenDays: false, activeRange: null };
  }

  // Validate and parse Firebase order data. En cas d'échec Zod, on retourne les données telles quelles
  // pour ne pas faire planter l'affichage (champs inconnus, enums évolutifs, etc.).
  private static validateFirebaseOrder(data: any): FirebaseOrder {
    const result = FirebaseOrderSchema.safeParse(data);
    if (result.success) {
      return result.data;
    }
    console.warn('⚠️ Firebase order data did not pass Zod validation, using raw data for display:', result.error.flatten?.() ?? result.error.message);
    return data as FirebaseOrder;
  }

  // Validate and parse product data. En cas d'échec, on retourne les données pour ne pas bloquer l'UI.
  private static validateProduct(data: any): Product {
    const result = ProductSchema.safeParse(data);
    if (result.success) {
      return result.data;
    }
    console.warn('⚠️ Product data did not pass Zod validation, using raw data:', result.error.flatten?.() ?? result.error.message);
    return data as Product;
  }

  /** Vérifie que l'adresse expéditeur a les champs obligatoires pour Sendcloud v3 */
  private static isValidSenderAddress(sa: { name?: string; address_line_1?: string; postal_code?: string; city?: string; country_code?: string }): boolean {
    return Boolean(sa?.name?.trim() && sa?.address_line_1?.trim() && sa?.postal_code?.trim() && sa?.city?.trim() && sa?.country_code?.trim());
  }

  /** Parse la date de création d'une commande (createdAt ou lastProductCreatedAt). */
  private static getOrderCreatedAt(order: FirebaseOrder): Date | null {
    const raw = order.createdAt ?? (order as any).lastProductCreatedAt;
    if (raw == null) return null;
    if (typeof (raw as any).toDate === 'function') return (raw as any).toDate();
    if (raw instanceof Date) return raw;
    try {
      const d = new Date(raw as any);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  /**
   * Récupère la dernière commande payée (deliveryInfo.paymentStatus === 'paid') pour fusion possible.
   * Tri par lastProductCreatedAt décroissant (pas par plage de dates).
   * Le fait d'être encore dans la période (mergeConfigSnapshot.periodEnd) est vérifié par l'appelant.
   */
  static async findMergeableOrderByCustomerId(customerId: string, _mergeConfig?: OrderMergeConfig): Promise<{ order: FirebaseOrder | null; isInOpenDays: boolean }> {
    try {
      const organizationId = this.getOrganizationId();
      console.log(`🗓️ Looking for last paid order by customerId: ${customerId} in organization: ${organizationId}`);

      const q = query(
        collection(db, OrderRepository.getCollectionName()),
        and(
          where('organizationId', '==', organizationId),
          where('customerId', '==', customerId),
          where('deliveryInfo.paymentStatus', '==', 'paid')
        ),
        orderBy('lastProductCreatedAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const orderDoc = querySnapshot.docs[0];
        const orderData = {
          id: orderDoc.id,
          ...orderDoc.data(),
        };
        console.log(`✅ Found last paid order by customerId: ${orderData.id}`);
        return { order: this.validateFirebaseOrder(orderData), isInOpenDays: !!(orderData as any).mergeConfigSnapshot?.wasCreatedInOpenDays };
      }

      console.log('🚫 No paid order found by customerId');
      return { order: null, isInOpenDays: false };
    } catch (_error) {
      console.error('❌ Error finding mergeable order by customerId:', _error);
      return { order: null, isInOpenDays: false };
    }
  }


  /**
   * Récupère la dernière commande payée (deliveryInfo.paymentStatus === 'paid') pour fusion possible.
   * Tri par lastProductCreatedAt décroissant (pas par plage de dates).
   * Le fait d'être encore dans la période (mergeConfigSnapshot.periodEnd) est vérifié par l'appelant.
   */
  static async findMergeableOrderByEmail(email: string, _mergeConfig?: OrderMergeConfig): Promise<{ order: FirebaseOrder | null; isInOpenDays: boolean }> {
    try {
      const organizationId = this.getOrganizationId();
      console.log(`🗓️ findMergeableOrderByEmail - Last paid order for email: ${email}, org: ${organizationId}`);

      const q = query(
        collection(db, OrderRepository.getCollectionName()),
        and(
          where('organizationId', '==', organizationId),
          where('orderInfo.email', '==', email),
          where('deliveryInfo.paymentStatus', '==', 'paid')
        ),
        orderBy('lastProductCreatedAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      console.log(`🗓️ Nombre de résultats: ${querySnapshot.size}`);

      if (!querySnapshot.empty) {
        const orderDoc = querySnapshot.docs[0];
        const orderData = {
          id: orderDoc.id,
          ...orderDoc.data(),
        };
        console.log(`✅ Found last paid order: ${orderData.id}`);
        return { order: this.validateFirebaseOrder(orderData), isInOpenDays: !!(orderData as any).mergeConfigSnapshot?.wasCreatedInOpenDays };
      }

      console.log('🚫 No paid order found for email');
      return { order: null, isInOpenDays: false };
    } catch (_error) {
      console.error('❌ Error finding mergeable order by email:', _error);
      return { order: null, isInOpenDays: false };
    }
  }

  // Add product to existing order
  static async addProductToOrder(orderId: string, newProduct: Product): Promise<void> {
    try {
      // Validate product data
      const validatedProduct = this.validateProduct(newProduct);

      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);
      const orderSnap = await getDoc(orderRef);

      if (orderSnap.exists()) {
        const orderData = this.validateFirebaseOrder({
          id: orderSnap.id,
          ...orderSnap.data(),
        });

        const updatedProducts = [...orderData.products, validatedProduct];

        await updateDoc(orderRef, {
          products: updatedProducts,
          updatedAt: serverTimestamp(),
          lastProductCreatedAt: serverTimestamp(),
        });

        console.log('✅ Product added to existing order:', orderId);
      }
    } catch (_error) {
      console.error('❌ Error adding product to order:', _error);
      throw new Error('Failed to add product to existing order');
    }
  }

  // Create a new order or merge with existing same-day order
  static async createOrder(orderData: OrderData, firebaseUid?: string): Promise<{ orderId: string; isMerged: boolean }> {
    try {
      console.log('🔍 Firebase createOrder called with:', orderData.email, firebaseUid ? `(UID: ${firebaseUid})` : '(guest)');

      // Trouver ou créer le customer
      const customer = await CustomerService.findOrCreateCustomer(orderData.email, firebaseUid);
      console.log(`✅ Customer trouvé/créé: ${customer.id}`);

      const organizationId = this.getOrganizationId();

      // Note: customerOrgs est géré automatiquement par les Cloud Functions backend
      // lors du paiement de la commande. Pas besoin de créer une affiliation ici.

      // Create product object
      const newProduct: Product = {
        reference: orderData.productReference,
        model: orderData.productModel || '',
        amount: orderData.amount,
        tiktokUsername: orderData.tiktokUsername,
        paymentInfo: {
          status: 'pending'
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Validate product data
      const validatedProduct = this.validateProduct(newProduct);

      // Charger la config orga (pour règles de fusion et adresse expéditeur)
      const orgaConfig = await OrgaConfigurationRepository.getByOrganizationId(organizationId);
      const mergeConfig = orgaConfig?.shippingConfig?.orderMergeConfig;

      // Chercher une commande fusionnable (même jour ou dans la période "jours ouverts" si configurée)
      console.log('🔍 Checking for existing order...');
      console.log('🔍 Customer ID:', customer.id);
      console.log('🔍 Email:', orderData.email);
      console.log('🔍 mergeConfig:', mergeConfig);
      
      // Chercher d'abord par email (plus fiable car l'email est toujours présent)
      let { order: existingOrder, isInOpenDays } = await this.findMergeableOrderByEmail(orderData.email, mergeConfig);
      
      // Si pas trouvé par email, chercher par customerId
      if (!existingOrder) {
        const customerResult = await this.findMergeableOrderByCustomerId(customer.id, mergeConfig);
        existingOrder = customerResult.order;
        isInOpenDays = customerResult.isInOpenDays;
      }

      if (existingOrder) {
        console.log('📦 Existing order found:', existingOrder.id);
        console.log('📦 Existing order deliveryInfo.paymentStatus:', existingOrder.deliveryInfo.paymentStatus);
        console.log('📦 Existing order mergeConfigSnapshot:', existingOrder.mergeConfigSnapshot);
        console.log('📦 Existing order createdAt:', existingOrder.createdAt);

        // Vérifier si la commande a un snapshot de config (nouvelle version)
        // Si pas de snapshot = ancienne commande → logique legacy (même jour uniquement)
        const hasSnapshot = !!existingOrder.mergeConfigSnapshot;
        console.log('📦 hasSnapshot:', hasSnapshot);

        if (hasSnapshot) {
          // Nouvelle logique : utiliser les DATES EXACTES stockées dans le snapshot
          const snapshot = existingOrder.mergeConfigSnapshot!;
          const now = new Date();
          
          // Vérifier si now est dans la période de fusion (dates exactes)
          const periodStart = snapshot.periodStart ? new Date(snapshot.periodStart) : null;
          const periodEnd = snapshot.periodEnd ? new Date(snapshot.periodEnd) : null;
          
          let canMerge = false;
          
          if (periodStart && periodEnd) {
            // Vérifier si maintenant est dans la période exacte
            const isInPeriod = now >= periodStart && now <= periodEnd;
            
            if (isInPeriod) {
              console.log(`🗓️ Dans la période de fusion (${format(periodStart, 'yyyy-MM-dd HH:mm')} → ${format(periodEnd, 'yyyy-MM-dd HH:mm')}) → fusion autorisée`);
              canMerge = true;
            } else {
              console.log(`🗓️ Hors période de fusion (${format(periodStart, 'yyyy-MM-dd HH:mm')} → ${format(periodEnd, 'yyyy-MM-dd HH:mm')}) → nouvelle commande`);
              canMerge = false;
            }
          } else {
            // Fallback si pas de dates dans le snapshot (ancien format) → même jour
            const todayStart = startOfDay(now);
            const todayEnd = endOfDay(now);
            const createdAtDate = existingOrder.createdAt?.toDate ? existingOrder.createdAt.toDate() : new Date(existingOrder.createdAt);
            const isSameDay = createdAtDate >= todayStart && createdAtDate <= todayEnd;
            
            if (isSameDay) {
              console.log('📅 Snapshot sans dates, même jour → fusion autorisée');
              canMerge = true;
            } else {
              console.log('📅 Snapshot sans dates, jour différent → nouvelle commande');
              canMerge = false;
            }
          }

          if (existingOrder.deliveryInfo.paymentStatus === 'paid' && canMerge) {
            console.log('🔄 Adding product to existing order (merged via snapshot config)...');
            await this.addProductToOrder(existingOrder.id!, validatedProduct);
            return { orderId: existingOrder.id!, isMerged: true };
          }
          if (existingOrder.deliveryInfo.paymentStatus === 'paid' && !canMerge) {
            console.log('🔄 Merge criteria not met (snapshot config), creating new order...');
          } else if (existingOrder.deliveryInfo.paymentStatus !== 'paid') {
            console.log('💰 Delivery not paid yet, creating new order...');
          }
        } else {
          // Ancienne logique (legacy) : fusion uniquement si même jour
          // On utilise createdAt pour vérifier si c'est le même jour
          const todayStart = startOfDay(new Date());
          const todayEnd = endOfDay(new Date());
          const createdAtDate = existingOrder.createdAt?.toDate ? existingOrder.createdAt.toDate() : new Date(existingOrder.createdAt);
          const isSameDay = createdAtDate >= todayStart && createdAtDate <= todayEnd;
          
          console.log('📅 Legacy check - todayStart:', todayStart);
          console.log('📅 Legacy check - todayEnd:', todayEnd);
          console.log('📅 Legacy check - createdAtDate:', createdAtDate);
          console.log('📅 Legacy check - isSameDay:', isSameDay);
          console.log('📅 Legacy check - paymentStatus:', existingOrder.deliveryInfo.paymentStatus);

          if (existingOrder.deliveryInfo.paymentStatus === 'paid' && isSameDay) {
            console.log('🔄 Adding product to existing order (merged via legacy same-day logic)...');
            await this.addProductToOrder(existingOrder.id!, validatedProduct);
            return { orderId: existingOrder.id!, isMerged: true };
          }
          if (existingOrder.deliveryInfo.paymentStatus === 'paid' && !isSameDay) {
            console.log('🔄 Legacy order not same day, creating new order...');
          } else {
            console.log('💰 Delivery not paid yet, creating new order...');
          }
        }
      }

      console.log('📦 Creating new order...');
      const sendcloudSenderAddress = orgaConfig?.shippingConfig?.senderAddress ?? null;
      const sendcloudSenderAddressId = sendcloudSenderAddress?.sendcloudId ?? null;

      // Snapshot de la config de fusion (pour que les règles soient figées au moment de la création)
      // On stocke les DATES EXACTES de la période de fusion (pas les jours de la semaine)
      // Ainsi, une commande créée Ven 14 fév ne sera fusionnable que jusqu'à Dim 16 fév, pas Ven 21 fév
      // IMPORTANT: On crée TOUJOURS un snapshot, même sans config (valeurs par défaut)
      const { start: periodStart, end: periodEnd, isInOpenDays: wasCreatedInOpenDays } = this.getMergeDateRange(mergeConfig);
      console.log('📋 mergeConfig:', mergeConfig);
      console.log('📋 wasCreatedInOpenDays:', wasCreatedInOpenDays);
      console.log('📋 periodStart:', periodStart);
      console.log('📋 periodEnd:', periodEnd);
      // Toujours créer un snapshot (même sans config = valeurs par défaut pour fusion même jour)
      // Logique simplifiée : 
      // - Si dans jours ouverts → fusion possible si now est entre periodStart et periodEnd
      // - Sinon → fusion le même jour uniquement (periodStart = periodEnd = jour de création)
      const mergeConfigSnapshot = {
        // Stocker les dates exactes de la période (pas les jours de la semaine)
        periodStart: periodStart.toISOString(),
        periodEnd: periodEnd.toISOString(),
        wasCreatedInOpenDays,
      };
      console.log('📋 mergeConfigSnapshot to save:', mergeConfigSnapshot);

      // Create new order with products array
      const firebaseOrder: Omit<FirebaseOrder, 'id'> = {
        customerId: customer.id, // Ajouter customerId
        organizationId,
        ...(orderData.organizationName ? { organizationName: orderData.organizationName } : {}),
        orderInfo: {
          firstName: orderData.firstName?.trim() || '',
          lastName: orderData.lastName?.trim() || '',
          email: orderData.email?.trim() || '',
          phone: orderData.phone?.trim() || '',
        },
        products: [validatedProduct], // Start with array containing one product
        deliveryInfo: {
          method: orderData.deliveryMethod,
          shippingCost: orderData.shippingCost || 0,
          address: orderData.address?.trim() || '',
          // Si fullAddress est manquant, on le construit manuellement
          fullAddress: orderData.fullAddress?.trim() || [
            orderData.houseNumber?.trim(),
            orderData.address?.trim(),
            orderData.postalCode?.trim(),
            orderData.city?.trim(),
            orderData.country?.trim()
          ].filter(Boolean).join(', '),
          houseNumber: orderData.houseNumber?.trim() || '',
          city: orderData.city?.trim() || '',
          postalCode: orderData.postalCode?.trim() || '',
          paymentStatus: 'pending',
          sendcloudSenderAddressId,
          sendcloudSenderAddress: sendcloudSenderAddress && this.isValidSenderAddress(sendcloudSenderAddress) ? sendcloudSenderAddress : null,
        },
        currentStep: 1,
        // Snapshot de la config de fusion au moment de la création (toujours présent)
        mergeConfigSnapshot,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastProductCreatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, OrderRepository.getCollectionName()), firebaseOrder);
      console.log('✅ New order created with ID:', docRef.id);

      return { orderId: docRef.id, isMerged: false };
    } catch (_error) {
      console.error('❌ Error creating order:', _error);
      throw new Error('Failed to create order');
    }
  }

  // Update delivery address
  static async updateDeliveryAddress(orderId: string, addressData: {
    address: string;
    fullAddress?: string;
    houseNumber?: string;
    addressLine2?: string;
    postalCode: string;
    city: string;
    country?: string;
  }): Promise<void> {
    try {
      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);

      const computedFullAddress = (addressData.fullAddress ?? `${(addressData.houseNumber ?? '').trim()} ${String(addressData.address || '').trim()}`).trim();

      const updateData: any = {
        'deliveryInfo.address': (addressData.address || '').trim(),
        'deliveryInfo.fullAddress': computedFullAddress || null,
        'deliveryInfo.postalCode': (addressData.postalCode || '').trim(),
        'deliveryInfo.city': (addressData.city || '').trim(),
        'deliveryInfo.houseNumber': (addressData.houseNumber ?? '').trim(),
        'deliveryInfo.addressLine2': (addressData.addressLine2 || '').trim() || null,
        'deliveryInfo.country': addressData.country,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(orderRef, updateData);
      console.log(`✅ Delivery address updated for order ${orderId}`);
    } catch (error) {
      console.error('❌ Error updating delivery address:', error);
      throw new Error('Failed to update delivery address');
    }
  }

  /**
   * Marque l'étiquette d'expédition comme "imprimée" dans deliveryInfo.
   * Utilisé côté admin lors de l'impression (1 ou plusieurs étiquettes).
   */
  static async markLabelPrinted(orderId: string): Promise<void> {
    await this.markLabelsPrinted([orderId]);
  }

  /**
   * Marque plusieurs étiquettes comme "imprimées" en batch Firestore.
   */
  static async markLabelsPrinted(orderIds: string[]): Promise<void> {
    const ids = (orderIds || []).filter(Boolean);
    if (ids.length === 0) return;

    try {
      const batch = writeBatch(db);
      for (const id of ids) {
        const orderRef = doc(db, OrderRepository.getCollectionName(), id);
        batch.update(orderRef, {
          'parcelGenerated.labelPrinted': true,
          'parcelGenerated.labelPrintedAt': serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      await batch.commit();
      console.log(`✅ labelPrinted marqué pour ${ids.length} commande(s)`);
    } catch (error) {
      console.error('❌ Error marking labelPrinted:', error);
      throw new Error('Failed to mark label as printed');
    }
  }

  // Get order by ID
  static async getOrder(orderId: string): Promise<FirebaseOrder | null> {
    try {
      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);
      const orderSnap = await getDoc(orderRef);

      if (orderSnap.exists()) {
        const orderData = {
          id: orderSnap.id,
          ...orderSnap.data(),
        };

        return this.validateFirebaseOrder(orderData);
      }
      return null;
    } catch (_error) {
      console.error('Error getting order:', _error);
      throw new Error('Failed to get order');
    }
  }

  /**
   * Récupère toutes les commandes d'un customer par customerId (toutes organisations)
   */
  static async getOrdersByCustomerId(customerId: string): Promise<FirebaseOrder[]> {
    try {
      console.log(`🔍 Récupération des commandes par customerId: ${customerId}`);

      // Requête sans orderBy pour éviter l'index composite (on triera côté client)
      const q = query(
        collection(db, OrderRepository.getCollectionName()),
        where('customerId', '==', customerId)
      );

      const querySnapshot = await getDocs(q);
      const orders: FirebaseOrder[] = [];

      querySnapshot.forEach((doc) => {
        const data = {
          id: doc.id,
          ...doc.data(),
        };
        orders.push(this.validateFirebaseOrder(data));
      });

      // Trier côté client par createdAt (plus récent en premier)
      orders.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt.getTime() :
          (a.createdAt as any)?.toDate ? (a.createdAt as any).toDate().getTime() : 0;
        const dateB = b.createdAt instanceof Date ? b.createdAt.getTime() :
          (b.createdAt as any)?.toDate ? (b.createdAt as any).toDate().getTime() : 0;
        return dateB - dateA; // Descendant
      });

      console.log(`✅ ${orders.length} commandes trouvées pour customerId: ${customerId}`);
      return orders;
    } catch (error) {
      console.error(`❌ Erreur lors de la récupération des commandes par customerId:`, error);
      throw error;
    }
  }

  // Get orders by email (rétrocompatibilité - pour une organisation spécifique)
  static async getOrdersByEmail(email: string): Promise<FirebaseOrder[]> {
    try {
      const organizationId = this.getOrganizationId();
      const q = query(
        collection(db, OrderRepository.getCollectionName()),
        and(
          where('organizationId', '==', organizationId),
          where('orderInfo.email', '==', email)
        )
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => {
        const orderData = {
          id: doc.id,
          ...doc.data(),
        };
        return this.validateFirebaseOrder(orderData);
      });
    } catch (_error) {
      console.error('Error getting orders by email:', _error);
      throw new Error('Failed to get orders');
    }
  }

  // Get all orders (for admin use)
  static async getAllOrders(): Promise<FirebaseOrder[]> {
    try {
      const organizationId = this.getOrganizationId();
      const q = query(
        collection(db, OrderRepository.getCollectionName()),
        where('organizationId', '==', organizationId),
        orderBy('lastProductCreatedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => {
        const orderData = {
          id: doc.id,
          ...doc.data(),
        };
        return this.validateFirebaseOrder(orderData);
      });
    } catch (_error) {
      console.error('Error getting all orders:', _error);
      throw new Error('Failed to get all orders');
    }
  }

  static async getOrdersByDateRange(startDate: Date, endDate: Date): Promise<FirebaseOrder[]> {
    try {
      const organizationId = this.getOrganizationId();
      const q = query(
        collection(db, OrderRepository.getCollectionName()),
        where('organizationId', '==', organizationId),
        where('lastProductCreatedAt', '>=', startDate),
        where('lastProductCreatedAt', '<=', endDate),
        orderBy('lastProductCreatedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const orderData = {
          id: doc.id,
          ...doc.data(),
        };
        return this.validateFirebaseOrder(orderData);
      });
    } catch (_error) {
      console.error('Error getting orders by date range:', _error);
      throw new Error('Failed to get orders by date range');
    }
  }

  /**
   * Abonnement temps réel aux commandes dans une plage de dates (lastProductCreatedAt).
   * Retourne une fonction pour se désabonner.
   */
  static subscribeToOrdersByDateRange(
    startDate: Date,
    endDate: Date,
    callback: (orders: FirebaseOrder[]) => void
  ): () => void {
    const organizationId = this.getOrganizationId();
    const q = query(
      collection(db, OrderRepository.getCollectionName()),
      where('organizationId', '==', organizationId),
      where('lastProductCreatedAt', '>=', startDate),
      where('lastProductCreatedAt', '<=', endDate),
      orderBy('lastProductCreatedAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const orders = querySnapshot.docs.map(doc => {
        const orderData = {
          id: doc.id,
          ...doc.data(),
        };
        return this.validateFirebaseOrder(orderData);
      });
      callback(orders);
    }, (error) => {
      console.error('Error in subscribeToOrdersByDateRange:', error);
    });
  }

  /**
   * Paginé : commandes par plage de dates (lastProductCreatedAt).
   * Utilisé par le dashboard admin pour limiter les lectures Firestore.
   */
  static async getOrdersByDateRangePaginated(
    organizationId: string,
    startDate: Date,
    endDate: Date,
    pageLimit: number,
    startAfterDoc?: DocumentSnapshot | null
  ): Promise<{ items: FirebaseOrder[]; lastDoc: DocumentSnapshot | null; hasMore: boolean }> {
    try {
      const coll = collection(db, OrderRepository.getCollectionName());
      const base = [
        where('organizationId', '==', organizationId),
        where('lastProductCreatedAt', '>=', startDate),
        where('lastProductCreatedAt', '<=', endDate),
        orderBy('lastProductCreatedAt', 'desc'),
        limit(pageLimit),
      ] as const;
      const q = startAfterDoc
        ? query(coll, ...base, startAfter(startAfterDoc))
        : query(coll, ...base);
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => {
        const orderData = { id: d.id, ...d.data() };
        return this.validateFirebaseOrder(orderData);
      });
      const last = snapshot.docs.at(-1) ?? null;
      const hasMore = snapshot.docs.length === pageLimit;
      return { items, lastDoc: last, hasMore };
    } catch (e) {
      console.error('getOrdersByDateRangePaginated:', e);
      throw new Error('Failed to get orders by date range (paginated)');
    }
  }

  // Real-time listener for all orders (for admin dashboard)
  static subscribeToAllOrders(callback: (orders: FirebaseOrder[]) => void): () => void {
    const organizationId = this.getOrganizationId();
    const q = query(
      collection(db, OrderRepository.getCollectionName()),
      where('organizationId', '==', organizationId),
      orderBy('lastProductCreatedAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const orders = querySnapshot.docs.map(doc => {
        const orderData = {
          id: doc.id,
          ...doc.data(),
        };
        return this.validateFirebaseOrder(orderData);
      });

      callback(orders);
    }, (error) => {
      console.error('Error in orders subscription:', error);
    });
  }

  // Real-time listener for orders sorted by lastProductCreatedAt (for Live Assistant)
  static subscribeToOrdersByLastProductCreatedAt(callback: (orders: FirebaseOrder[]) => void): () => void {
    const organizationId = this.getOrganizationId();
    const q = query(
      collection(db, OrderRepository.getCollectionName()),
      where('organizationId', '==', organizationId),
      orderBy('lastProductCreatedAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const orders = querySnapshot.docs.map(doc => {
        const orderData = {
          id: doc.id,
          ...doc.data(),
        };
        return this.validateFirebaseOrder(orderData);
      });

      callback(orders);
    }, (error) => {
      console.error('Error in orders subscription (lastProductCreatedAt):', error);
    });
  }

  // Update delivery information (Step 2)
  static async updateDeliveryInfo(params: UpdateDeliveryInfoParams): Promise<void> {
    const {
      orderId,
      deliveryMethod,
      shippingCost,
      address,
      houseNumber,
      addressLine2,
      city,
      postalCode,
      country,
      pickupPoint,
      weight,
      methodName,
      methodDescription,
      provider,
      methodId, // ID générique de la méthode de livraison (provider-agnostic)
      estimatedHours,
      minWeight,
      maxWeight,
      servicePointInput,
      isHomeDelivery,
      // Informations complètes de la méthode de livraison
      methodPrice,
      methodCurrency,
      methodStatus
    } = params;
    try {
      console.log('📥 Updating delivery info:', {
        orderId,
        deliveryMethod,
        shippingCost,
        address,
        city,
        postalCode,
        pickupPoint
      });

      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);

      // Get current order to check if delivery is already paid
      const orderDoc = await getDoc(orderRef);
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }

      const orderData = this.validateFirebaseOrder({
        id: orderDoc.id,
        ...orderDoc.data(),
      });

      const isDeliveryAlreadyPaid = orderData.deliveryInfo.paymentStatus === 'paid';

      // Récupérer l'adresse expéditeur Sendcloud (from_address) depuis la config orga
      const orgaConfig = await OrgaConfigurationRepository.getByOrganizationId(orderData.organizationId);
      const sendcloudSenderAddress = orgaConfig?.shippingConfig?.senderAddress ?? null;
      const sendcloudSenderAddressId = sendcloudSenderAddress?.sendcloudId ?? null;

      const resolvedProvider =
        provider ?? orderData.deliveryInfo?.provider ?? 'sendcloud';

      const safeNumericMethodId: number | null = (() => {
        if (methodId == null) return null;
        if (typeof methodId === 'number') {
          return Number.isFinite(methodId) ? methodId : null;
        }
        const n = Number(methodId);
        return Number.isFinite(n) ? n : null;
      })();

      const dmStored = deliveryMethod ?? orderData.deliveryInfo?.deliveryMethod ?? null;
      const updateData: any = {
        'deliveryInfo.methodName': methodName || null,
        'deliveryInfo.methodDescription': methodDescription || null,
        'deliveryInfo.provider': resolvedProvider,
        'deliveryInfo.deliveryMethod': dmStored,
        /** Doublon utile (MR / ids string) — le backend résout deliveryMethod ou method */
        'deliveryInfo.method': dmStored,
        'deliveryInfo.methodId': safeNumericMethodId,
        'deliveryInfo.estimatedHours': estimatedHours || null,
        'deliveryInfo.minWeight': minWeight || null,
        'deliveryInfo.maxWeight': maxWeight || null,
        'deliveryInfo.servicePointInput': servicePointInput || null,
        'deliveryInfo.isHomeDelivery': isHomeDelivery || null,
        'deliveryInfo.shippingCost': shippingCost || 0,
        'deliveryInfo.address': address || '',
        'deliveryInfo.fullAddress': (params.fullAddress ?? null),
        'deliveryInfo.houseNumber': houseNumber || '',
        'deliveryInfo.addressLine2': addressLine2 || null,
        'deliveryInfo.city': city || '',
        'deliveryInfo.postalCode': postalCode || '',
        'deliveryInfo.country': country || 'FR',
        'deliveryInfo.pickupPoint': pickupPoint || null,
        'deliveryInfo.weight': weight || 0.5,
        // Informations complètes de la méthode de livraison
        // Important: conserver 0 et ne pas écraser avec null si non fourni.
        'deliveryInfo.methodPrice': methodPrice ?? orderData.deliveryInfo?.methodPrice ?? null,
        'deliveryInfo.methodCurrency': methodCurrency ?? orderData.deliveryInfo?.methodCurrency ?? null,
        'deliveryInfo.methodStatus': methodStatus ?? orderData.deliveryInfo?.methodStatus ?? null,
        'deliveryInfo.sendcloudSenderAddressId': sendcloudSenderAddressId,
        'deliveryInfo.sendcloudSenderAddress': OrderRepository.isValidSenderAddress(sendcloudSenderAddress || {}) ? sendcloudSenderAddress : null,
        currentStep: 2,
        updatedAt: serverTimestamp(),
      };

      // Only set delivery payment status to pending for new orders
      // Don't change it for merged orders where delivery is already paid
      if (!isDeliveryAlreadyPaid) {
        updateData['deliveryInfo.paymentStatus'] = 'pending';
      }

      console.log('📤 Sending update to Firebase:', updateData);

      await updateDoc(orderRef, updateData);
      console.log('✅ Delivery info updated successfully');
    } catch (_error) {
      console.error('❌ Error updating delivery info:', _error);
      throw new Error('Failed to update delivery information');
    }
  }

  /**
   * Écrit deliveryMethod + method (id config orga) et retire methodId invalide (NaN).
   * À appeler après fusion ou pour corriger d’anciennes commandes Mondial Relay (id string, pas Sendcloud).
   */
  static async ensureShippingConfigIdsOnOrder(orderId: string): Promise<void> {
    try {
      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);
      const orderDoc = await getDoc(orderRef);
      if (!orderDoc.exists()) return;

      const di = orderDoc.data()?.deliveryInfo as Record<string, unknown> | undefined;
      if (!di) return;

      const resolved = resolveShippingMethodConfigId(di);
      if (!resolved) return;

      const patch: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        'deliveryInfo.deliveryMethod': resolved,
        'deliveryInfo.method': resolved,
      };

      const mid = di.methodId;
      if (typeof mid === 'number' && !Number.isFinite(mid)) {
        patch['deliveryInfo.methodId'] = deleteField();
      }

      await updateDoc(orderRef, patch);
      console.log(`✅ ensureShippingConfigIdsOnOrder: ${orderId} → ${resolved}`);
    } catch (e) {
      console.warn('ensureShippingConfigIdsOnOrder:', e);
    }
  }

  // Update payment information for specific product
  static async updateProductPaymentInfo(
    orderId: string,
    productIndex: number,
    method: 'stripe' | 'paypal',
    paymentIntentId?: string,
    settlement_status?: 'awaiting_transfer' | 'transferred' | 'failed',
    commissionRate?: number,
    fixedCommission?: number,
    currency?: string
  ): Promise<void> {
    try {
      console.log('💳 Updating product payment info:', {
        orderId,
        productIndex,
        method,
        paymentIntentId,
        commissionRate,
        fixedCommission,
        currency
      });

      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);
      const orderDoc = await getDoc(orderRef);

      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }

      const orderData = this.validateFirebaseOrder({
        id: orderDoc.id,
        ...orderDoc.data(),
      });

      const products = [...orderData.products];

      if (!products[productIndex]) {
        throw new Error('Product not found at specified index');
      }

      // Update the specific product's payment info
      products[productIndex] = {
        ...products[productIndex],
        paymentInfo: {
          ...products[productIndex].paymentInfo,
          method,
          ...(paymentIntentId && { paymentIntentId }), // Only include if not undefined
          ...(settlement_status && { settlement_status }), // Only include if not undefined
          ...(commissionRate !== undefined && { commissionRate }), // Taux de commission en pourcentage
          ...(fixedCommission !== undefined && { fixedCommission }), // Commission fixe en centimes (EUR)
          ...(currency && { currency }), // Devise (par défaut "eur")
        },
        updatedAt: new Date().toISOString()
      };

      await updateDoc(orderRef, {
        products: products,
        currentStep: 3,
        updatedAt: serverTimestamp(),
      });

      console.log('✅ Product payment info updated successfully');
    } catch (_error) {
      console.error('❌ Error updating product payment info:', _error);
      throw new Error('Failed to update product payment info');
    }
  }

  // Complete order (called when payment is successful)
  static async completeOrder(orderId: string): Promise<void> {
    try {
      console.log('🏁 Completing order:', orderId);

      await updateDoc(doc(db, OrderRepository.getCollectionName(), orderId), {
        currentStep: 'complete',
        updatedAt: serverTimestamp(),
      });
    } catch (_error) {
      console.error('Error completing order:', _error);
      throw new Error('Failed to complete order');
    }
  }

  // Update order status
  static async updateOrderStatus(orderId: string, status: string): Promise<void> {
    try {
      await updateDoc(doc(db, OrderRepository.getCollectionName(), orderId), {
        status: status,
        updatedAt: serverTimestamp(),
      });
    } catch (_error) {
      console.error('Error updating order status:', _error);
      throw new Error('Failed to update order status');
    }
  }

  // Update payment status for a specific product
  static async updatePaymentStatus(orderId: string, productIndex: number, status: 'pending' | 'paid' | 'failed'): Promise<void> {
    try {
      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);
      const orderDoc = await getDoc(orderRef);

      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }

      const orderData = this.validateFirebaseOrder({
        id: orderDoc.id,
        ...orderDoc.data(),
      });

      const products = [...orderData.products];

      if (!products[productIndex]) {
        throw new Error('Product not found at specified index');
      }

      products[productIndex] = {
        ...products[productIndex],
        paymentInfo: {
          ...products[productIndex].paymentInfo,
          status,
          ...(status === 'paid' && { paidAt: new Date().toISOString() }),
          ...(status === 'failed' && { failedAt: new Date().toISOString() })
        },
        updatedAt: new Date().toISOString()
      };

      await updateDoc(orderRef, {
        products: products,
        updatedAt: serverTimestamp(),
      });
    } catch (_error) {
      console.error('Error updating payment status:', _error);
      throw new Error('Failed to update payment status');
    }
  }

  // Update delivery payment status
  static async updateDeliveryPaymentStatus(orderId: string, status: 'pending' | 'paid'): Promise<void> {
    try {
      await updateDoc(doc(db, OrderRepository.getCollectionName(), orderId), {
        'deliveryInfo.paymentStatus': status,
        updatedAt: serverTimestamp(),
      });
    } catch (_error) {
      console.error('Error updating delivery payment status:', _error);
      throw new Error('Failed to update delivery payment status');
    }
  }

  // Note: saveTrackingInfo supprimé - les informations de tracking sont maintenant dans deliveryInfo

  // Update order personal information
  static async updateOrderInfo(orderId: string, firstName: string, lastName: string, phone: string): Promise<void> {
    try {
      console.log('👤 Updating order personal info:', { orderId, firstName, lastName, phone });

      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);

      const updateData = {
        'orderInfo.firstName': firstName,
        'orderInfo.lastName': lastName,
        'orderInfo.phone': phone,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(orderRef, updateData);
      console.log('✅ Order personal info updated successfully');
    } catch (_error) {
      console.error('❌ Error updating order personal info:', _error);
      throw new Error('Failed to update order personal information');
    }
  }

  // Update preparation status for the entire order
  static async updateOrderPreparationStatus(orderId: string, status: 'todo' | 'in_progress' | 'ready'): Promise<void> {
    try {
      console.log('🍳 Updating order preparation status:', { orderId, status });

      const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);

      const updateData: any = {
        updatedAt: serverTimestamp(),
        preparationStatus: status
      };

      await updateDoc(orderRef, updateData);

      console.log('✅ Order preparation status updated successfully');
    } catch (_error) {
      console.error('❌ Error updating order preparation status:', _error);
      throw new Error('Failed to update order preparation status');
    }
  }

  /**
   * Clôture manuellement la période de fusion d'une commande en mettant periodEnd à maintenant.
   * Après cet appel, les nouvelles commandes du même client ne fusionneront plus avec celle-ci (frais de livraison à repayer).
   */
  static async closeOrderMergePeriod(orderId: string): Promise<void> {
    const orderRef = doc(db, OrderRepository.getCollectionName(), orderId);
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) {
      throw new Error('Order not found');
    }
    const data = orderDoc.data();
    const current = (data?.mergeConfigSnapshot ?? null) as { periodStart?: string; periodEnd?: string; wasCreatedInOpenDays?: boolean } | null;
    const nowIso = new Date().toISOString();
    const updatedSnapshot = current
      ? { ...current, periodEnd: nowIso }
      : { periodStart: nowIso, periodEnd: nowIso, wasCreatedInOpenDays: false };
    await updateDoc(orderRef, {
      mergeConfigSnapshot: updatedSnapshot,
      updatedAt: serverTimestamp(),
    });
  }

  // Credit management methods
  static async createCreditRefund(
    userId: string,
    orderId: string,
    productIndex: number,
    amount: number,
    reason: string
  ): Promise<string> {
    try {
      const functions = getFunctions();
      const createCreditRefundFunction = httpsCallable(functions, 'createCreditRefund');

      const result = await createCreditRefundFunction({
        userId,
        orderId,
        productIndex,
        amount,
        reason
      });

      return (result.data as { transactionId: string }).transactionId;
    } catch (_error) {
      console.error('❌ Erreur lors de la création de l\'avoir:', _error);
      throw new Error('Échec de la création de l\'avoir');
    }
  }

  static async getUserBalance(userId: string): Promise<number> {
    try {
      const functions = getFunctions();
      const getUserBalanceFunction = httpsCallable(functions, 'getUserBalance');

      const result = await getUserBalanceFunction({ userId });
      return (result.data as { balance: number }).balance;
    } catch (_error) {
      console.error('❌ Erreur lors de la récupération du solde:', _error);
      throw new Error('Échec de la récupération du solde');
    }
  }

  static async useCredit(userId: string, amount: number, orderId: string): Promise<void> {
    try {
      const functions = getFunctions();
      const useCreditCallable = httpsCallable(functions, 'useCredit');

      await useCreditCallable({
        userId,
        amount,
        orderId
      });
    } catch (_error) {
      console.error('❌ Erreur lors de l\'utilisation de l\'avoir:', _error);
      throw new Error('Échec de l\'utilisation de l\'avoir');
    }
  }

  static async getCreditTransactions(userId: string): Promise<CreditTransaction[]> {
    try {
      const functions = getFunctions();
      const getCreditTransactionsFunction = httpsCallable(functions, 'getCreditTransactions');

      const result = await getCreditTransactionsFunction({ userId });
      return (result.data as { transactions: CreditTransaction[] }).transactions;
    } catch (_error) {
      console.error('❌ Erreur lors de la récupération des transactions:', _error);
      throw new Error('Échec de la récupération des transactions');
    }
  }
}

